import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/returns")({
  head: () => ({
    meta: [
      { title: "Returns, Refund & Warranty Policy — banana.cards" },
      { name: "description", content: "How returns, refunds, replacements and NFC warranty work for Banana Cards physical products, subscriptions and Google Review cards." },
      { property: "og:title", content: "Returns, Refund & Warranty Policy — banana.cards" },
      { property: "og:description", content: "Return windows, faulty-product handling, NFC troubleshooting and subscription refunds at Banana Cards." },
    ],
    links: alternateLinkList("/legal/returns", "en"),
  }),
  component: ReturnsPage,
});

function ReturnsPage() {
  return (
    <LegalPage
      eyebrow="Legal · Trust"
      title="Returns, Refund &"
      italic="Warranty Policy."
      updated="January 2026"
      intro="Every Banana Cards order is prepared, encoded and inspected before it leaves us. Because many of our products are personalised, printed, encoded or linked to a customer profile, what can be returned depends on the type of product you purchased. This policy explains exactly how returns, refunds, replacements and warranty claims work."
      related={[
        { label: "Terms of Service", to: "/terms" },
        { label: "Privacy Policy", to: "/privacy" },
        { label: "Contact Support", to: "/contact" },
      ]}
      sections={[
        {
          id: "non-custom",
          title: "1. Returns for Non-Custom Products",
          body: (
            <>
              <P>Non-custom Banana Cards products may be returned within 7 days of delivery if they are:</P>
              <UL items={[
                "unused;",
                "unopened or in original packaging;",
                "in original, sellable condition;",
                "not activated, encoded, personalised, scratched, damaged, or altered.",
              ]} />
              <P>Customers are responsible for return shipping costs unless the product is faulty or the wrong item was supplied.</P>
              <P>Once the returned item has been inspected and approved, Banana Cards may issue a refund, replacement, or store credit.</P>
            </>
          ),
        },
        {
          id: "custom",
          title: "2. Custom, Personalised, Printed, or Encoded Cards",
          body: (
            <>
              <P>Custom cards, personalised cards, printed cards, engraved cards, encoded NFC cards, QR-linked cards, company-branded cards, Google Review Cards, and any card already activated or assigned to a customer profile are <strong>not eligible for return or refund</strong>, unless the item is faulty or incorrectly produced by Banana Cards.</P>
              <P>This is because these products are made or configured specifically for the customer and cannot be resold.</P>
            </>
          ),
        },
        {
          id: "faulty",
          title: "3. Faulty or Incorrect Products",
          body: (
            <>
              <P>If a product arrives faulty, damaged, incorrectly printed, incorrectly encoded, or materially different from the order confirmation, the customer must contact Banana Cards within 7 days of delivery.</P>
              <P>The customer may be asked to provide:</P>
              <UL items={[
                "order number;",
                "photos or video of the issue;",
                "description of the problem;",
                "proof of delivery or purchase.",
              ]} />
              <P>If the issue is confirmed to be caused by Banana Cards, we will offer one of the following:</P>
              <UL items={[
                "replacement;",
                "repair or re-encoding;",
                "store credit;",
                "refund where replacement or correction is not possible.",
              ]} />
            </>
          ),
        },
        {
          id: "nfc",
          title: "4. NFC Functionality",
          body: (
            <>
              <P>Banana Cards are designed to work with compatible NFC-enabled smartphones and devices. NFC performance may vary depending on the phone model, phone case, device settings, browser, operating system, or third-party app behaviour.</P>
              <P>A card will not be considered faulty if:</P>
              <UL items={[
                "the customer's phone does not support NFC;",
                "NFC is disabled on the customer's device;",
                "the card is blocked by a metal phone case or accessory;",
                "the issue is caused by incorrect setup, wrong link, or customer-side configuration;",
                "the card works correctly when tested on another compatible device.",
              ]} />
              <P>Where possible, Banana Cards may assist with troubleshooting, reconfiguration, or re-encoding.</P>
            </>
          ),
        },
        {
          id: "subscriptions",
          title: "5. Software Subscriptions",
          body: (
            <>
              <P>Banana Cards software subscriptions are billed according to the selected plan.</P>
              <P>Subscription fees are generally non-refundable once a billing cycle has started, unless required by law or unless Banana Cards confirms a technical issue that prevents access to the paid service and cannot be resolved.</P>
              <P>Customers may cancel their subscription before the next billing cycle to avoid future charges. Cancellation does not automatically refund previous paid periods.</P>
            </>
          ),
        },
        {
          id: "google-review",
          title: "6. Google Review Cards",
          body: (
            <>
              <P>Google Review Cards are configured using the customer's Google Business Profile review link.</P>
              <P>Banana Cards is responsible for the card hardware and NFC/QR setup, but is not responsible for:</P>
              <UL items={[
                "changes made by Google;",
                "suspension or removal of a Google Business Profile;",
                "incorrect links provided by the customer;",
                "reviews not being published by Google;",
                "customer review behaviour;",
                "Google account, permission, or business listing issues.",
              ]} />
              <P>If the wrong link was provided by the customer, Banana Cards may assist with updating the card where technically possible.</P>
            </>
          ),
        },
        {
          id: "refund-processing",
          title: "7. Refund Processing",
          body: (
            <>
              <P>Approved refunds will be processed to the original payment method where possible.</P>
              <P>Refund timing may vary depending on the payment provider, bank, or card issuer.</P>
              <P>Shipping fees, transaction fees, customisation fees, design fees, and completed setup services may be non-refundable unless the return is due to an error by Banana Cards.</P>
            </>
          ),
        },
        {
          id: "return-shipping",
          title: "8. Return Shipping",
          body: (
            <>
              <P>Unless the item is faulty or Banana Cards supplied the wrong product, the customer is responsible for return shipping.</P>
              <P>Returned items must be packed securely. Banana Cards is not responsible for items lost or damaged during return shipping.</P>
            </>
          ),
        },
        {
          id: "not-eligible",
          title: "9. Items Not Eligible for Return",
          body: (
            <>
              <P>The following items are not eligible for return unless faulty:</P>
              <UL items={[
                "custom printed cards;",
                "engraved metal cards;",
                "branded company cards;",
                "activated NFC cards;",
                "encoded or profile-linked cards;",
                "used or damaged cards;",
                "promotional, sample, or giveaway cards;",
                "software subscriptions already used during a billing period;",
                "design, setup, onboarding, or configuration services already completed.",
              ]} />
            </>
          ),
        },
        {
          id: "how-to-request",
          title: "10. How to Request a Return or Refund",
          body: (
            <>
              <P>To request support, customers should contact Banana Cards with:</P>
              <UL items={[
                "full name;",
                "order number;",
                "product purchased;",
                "reason for return or refund request;",
                "photos or videos where relevant.",
              ]} />
              <P>
                Email us at{" "}
                <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>
                {" "}and our team will review the request and confirm the next steps.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}